import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { useCallback, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { InterpretationValue } from 'models/measureInterpretation';
import { translateOnlyFallback, translateSeason } from 'utils/translateAttribute';
import { CustomMeasureProvider } from 'services/model/customMeasures';

export type TimeRef = 'OD' | 'ED';

export type AttributeCode =
  | 'prdName'
  | 'prdNumber'
  | 'prdCategory'
  | 'prdBrand'
  | 'prdSeason'
  | 'prdGender'
  | 'prdTargetAge'
  | 'artImageUrl'
  | 'artSize'
  | 'artBrandColor' // this is legacy
  | 'styBrandColor'
  | 'styNumber'
  | 'styName'
  | 'artEan'
  | 'chnName'
  | 'chnMarketplace'
  | 'chnCountry'
  | 'chnFulfillmentMethod';

interface AttributeBase {
  code: AttributeCode;
  translate?: (inti: IntlShape, value: string) => string;
}

const ATTRIBUTES: AttributeBase[] = [
  { code: 'prdName' },
  { code: 'prdNumber' },
  { code: 'prdBrand' },
  { code: 'artImageUrl' },
  {
    code: 'artSize',
    translate: (intl, value) => translateOnlyFallback(value, intl),
  },
  {
    code: 'artBrandColor',
    translate: (intl, value) => translateOnlyFallback(value, intl),
  },
  {
    code: 'styBrandColor',
    translate: (intl, value) => translateOnlyFallback(value, intl),
  },
  { code: 'artEan' },
  {
    code: 'prdCategory',
    translate: (intl, value) =>
      intl.formatMessage({
        id: `product.categories.${value}`,
        defaultMessage: value,
      }),
  },
  {
    code: 'prdSeason',
    translate: (intl, value) => translateSeason(value, intl),
  },
  {
    code: 'prdGender',
    translate: (intl, value) => intl.formatMessage({ id: `product.genders.${value ?? 'unknown'}` }),
  },
  {
    code: 'prdTargetAge',
    translate: (intl, value) =>
      intl.formatMessage({
        id: `product.ageGroup.${value ?? 'unknown'}`,
        defaultMessage: value,
      }),
  },
  { code: 'chnName' },
  { code: 'chnMarketplace' },
  {
    code: 'chnCountry',
    translate: (intl, value) =>
      intl.formatDisplayName(value?.toUpperCase(), {
        type: 'region',
      })!,
  },
  { code: 'chnFulfillmentMethod' },
] as const;

export type AttributeValues = Record<string, string>;

export interface Attribute {
  code: AttributeCode;
  name: string;
  getRawValue: (values?: AttributeValues) => string;
  getValue(values?: AttributeValues): string;
  translate: (value: string) => string;
}

export type MetricCode =
  | 'ordNum'
  | 'ordQty'
  | 'ordGmvVal'
  | 'avgOrdGmvVal'
  | 'cncQty'
  | 'cncQtyRate'
  | 'slsNum'
  | 'slsQty'
  | 'slsPrdNum'
  | 'slsGmvVal'
  | 'slsMkdRate'
  | 'slsAtOmvQtyRate'
  | 'retQty'
  | 'retQtyRate'
  | 'retGmvVal'
  | 'netSlsQty'
  | 'netSlsGmvVal'
  | 'netSlsGmvValRate'
  | 'itemNetSlsGmvVal'
  | 'cm1Rate'
  | 'cm2Rate'
  | 'cm1Val'
  | 'cm2Val'
  | 'netSlsCogRate'
  | 'netSlsCogVal'
  | 'netSlsFeeVal'
  | 'netSlsFeeRate'
  | 'netSlsLogCostVal'
  | 'netSlsLogCostRate'
  | 'netSlsMkpFeeVal'
  | 'netSlsMkpFeeRate'
  | 'slsPayFeeVal'
  | 'slsPayFeeRate'
  | 'retShpCstVal'
  | 'retShpCstRate'
  | 'retHdlCstVal'
  | 'retHdlCstRate'
  | 'slsShpCstVal'
  | 'slsShpCstRate'
  | 'slsHdlCstVal'
  | 'slsHdlCstRate'
  | 'itemSlsOmvVal'
  | 'itemSlsGmvVal'
  | 'itemSlsMkdRate'
  | 'itemSlsMkdVal'
  // To be still replaced
  | 'itemNetSlsGmvValExpOD';

export type MeasureCode =
  | MetricCode
  | 'slsMkdRateOD'
  | 'slsAtOmvQtyRateOD'
  | 'cm1RateExtExpOD'
  | 'cm1ValExtExpOD'
  | 'cm2RateExtExpOD'
  | 'cm2ValExtExpOD'
  | 'itemNetSlsCogValExtExpOD'
  | 'itemNetSlsFeeValExtExpOD'
  | 'itemNetSlsGmvValExpOD'
  | 'itemNetSlsLogCostValExtExpOD'
  | 'itemNetSlsMkpFeeValExtExpOD'
  | 'itemRetHdlCostValExtExpOD'
  | 'itemRetShpCostValExtExpOD'
  | 'itemSlsGmvValExtOD'
  | 'itemSlsHdlCostValOD'
  | 'itemSlsMkdRateExtOD'
  | 'itemSlsMkdValExtOD'
  | 'itemSlsOmvValExtOD'
  | 'itemSlsPayFeeValOD'
  | 'itemSlsShpCostValOD'
  | 'netSlsCogRateExtExpOD'
  | 'netSlsCogValExtExpOD'
  | 'netSlsFeeRateExtExpOD'
  | 'netSlsFeeValExtExpOD'
  | 'netSlsGmvValExpOD'
  | 'netSlsGmvValExtExpOD'
  | 'netSlsGmvValExt'
  | 'netSlsGmvValRateExpOD'
  | 'netSlsLogCostRateExtExpOD'
  | 'netSlsLogCostValExtExpOD'
  | 'netSlsMkpFeeRateExtExpOD'
  | 'netSlsMkpFeeValExtExpOD'
  | 'netSlsQtyExpOD'
  | 'retGmvValExpOD'
  | 'retHdlCstRateExtExpOD'
  | 'retHdlCstValExtExpOD'
  | 'retQtyExpOD'
  | 'retQtyRateExpOD'
  | 'retShpCstRateExtExpOD'
  | 'retShpCstValExtExpOD'
  | 'slsGmvValOD'
  | 'slsHdlCstRateExtOD'
  | 'slsHdlCstValOD'
  | 'slsNumOD'
  | 'slsPayFeeRateExtOD'
  | 'slsPayFeeValOD'
  | 'slsPrdNumOD'
  | 'slsQtyOD'
  | 'slsShpCstRateExtOD'
  | 'slsShpCstValOD';

interface MetricBase {
  code: string;
  unit: MeasureUnitCode;
  interpretation: InterpretationValue;
}

export interface MeasureBase {
  code: MeasureCode;
  metricCode: MetricCode;
  timeRef: TimeRef;
  onlyOD?: boolean;
  explicitTax?: boolean;
}

export const METRICS: MetricBase[] = [
  {
    code: 'ordNum',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'ordQty',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'ordGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'avgOrdGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'cncQty',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'cncQtyRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsQty',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'slsNum',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'slsPrdNum',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'slsGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'slsMkdRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsAtOmvQtyRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'retQty',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retQtyRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsQty',
    unit: MeasureUnitCode.QUANTITY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'netSlsGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'netSlsGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'netSlsGmvValRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'itemNetSlsGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'cm1Rate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'cm2Rate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'cm1Val',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'cm2Val',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'netSlsCogRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsCogVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsFeeRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsFeeVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsLogCostRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsLogCostVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsMkpFeeVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'netSlsMkpFeeRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsPayFeeVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsPayFeeRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retShpCstVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retShpCstRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retHdlCstVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'retHdlCstRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsShpCstVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsShpCstRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsHdlCstVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'slsHdlCstRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'itemSlsOmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'itemSlsGmvVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Positive,
  },
  {
    code: 'itemSlsMkdRate',
    unit: MeasureUnitCode.PERCENT,
    interpretation: InterpretationValue.Negative,
  },
  {
    code: 'itemSlsMkdVal',
    unit: MeasureUnitCode.CURRENCY,
    interpretation: InterpretationValue.Negative,
  },
] as const;

export const MEASURES: MeasureBase[] = [
  {
    code: 'ordNum',
    metricCode: 'ordNum',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'ordQty',
    metricCode: 'ordQty',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'ordGmvVal',
    metricCode: 'ordGmvVal',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'avgOrdGmvVal',
    metricCode: 'avgOrdGmvVal',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'cncQty',
    metricCode: 'cncQty',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'cncQtyRate',
    metricCode: 'cncQtyRate',
    timeRef: 'OD',
    onlyOD: true,
  },
  {
    code: 'slsNum',
    metricCode: 'slsNum',
    timeRef: 'ED',
  },
  {
    code: 'slsNumOD',
    metricCode: 'slsNum',
    timeRef: 'OD',
  },
  {
    code: 'slsQty',
    metricCode: 'slsQty',
    timeRef: 'ED',
  },
  {
    code: 'slsQtyOD',
    metricCode: 'slsQty',
    timeRef: 'OD',
  },
  {
    code: 'slsPrdNum',
    metricCode: 'slsPrdNum',
    timeRef: 'ED',
  },
  {
    code: 'slsPrdNumOD',
    metricCode: 'slsPrdNum',
    timeRef: 'OD',
  },
  {
    code: 'slsGmvValOD',
    metricCode: 'slsGmvVal',
    timeRef: 'OD',
  },
  {
    code: 'retQtyExpOD',
    metricCode: 'retQty',
    timeRef: 'OD',
  },

  {
    code: 'retQty',
    metricCode: 'retQty',
    timeRef: 'ED',
  },
  {
    code: 'retQtyRate',
    metricCode: 'retQtyRate',
    timeRef: 'ED',
  },
  {
    code: 'netSlsQty',
    metricCode: 'netSlsQty',
    timeRef: 'ED',
  },
  {
    code: 'netSlsGmvVal',
    metricCode: 'netSlsGmvVal',
    timeRef: 'ED',
  },
  {
    code: 'netSlsGmvValExt',
    metricCode: 'netSlsGmvVal',
    timeRef: 'ED',
    explicitTax: true,
  },
  {
    code: 'retGmvVal',
    metricCode: 'retGmvVal',
    timeRef: 'ED',
  },
  {
    code: 'retGmvValExpOD',
    metricCode: 'retGmvVal',
    timeRef: 'OD',
  },
  {
    code: 'retQtyRateExpOD',
    metricCode: 'retQtyRate',
    timeRef: 'OD',
  },
  {
    code: 'netSlsQtyExpOD',
    metricCode: 'netSlsQty',
    timeRef: 'OD',
  },
  {
    code: 'netSlsGmvValExpOD',
    metricCode: 'netSlsGmvVal',
    timeRef: 'OD',
  },
  {
    code: 'netSlsGmvValExtExpOD',
    metricCode: 'netSlsGmvVal',
    timeRef: 'OD',
    explicitTax: true,
  },
  {
    code: 'netSlsGmvValRateExpOD',
    metricCode: 'netSlsGmvValRate',
    timeRef: 'OD',
  },
  {
    code: 'cm1RateExtExpOD',
    metricCode: 'cm1Rate',
    timeRef: 'OD',
  },
  {
    code: 'cm2RateExtExpOD',
    metricCode: 'cm2Rate',
    timeRef: 'OD',
  },
  {
    code: 'cm1ValExtExpOD',
    metricCode: 'cm1Val',
    timeRef: 'OD',
  },
  {
    code: 'cm2ValExtExpOD',
    metricCode: 'cm2Val',
    timeRef: 'OD',
  },
  {
    code: 'netSlsCogRateExtExpOD',
    metricCode: 'netSlsCogRate',
    timeRef: 'OD',
  },
  {
    code: 'netSlsCogValExtExpOD',
    metricCode: 'netSlsCogVal',
    timeRef: 'OD',
  },
  {
    code: 'netSlsFeeRateExtExpOD',
    metricCode: 'netSlsFeeRate',
    timeRef: 'OD',
  },
  {
    code: 'netSlsFeeValExtExpOD',
    metricCode: 'netSlsFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'netSlsLogCostRateExtExpOD',
    metricCode: 'netSlsLogCostRate',
    timeRef: 'OD',
  },
  {
    code: 'netSlsMkpFeeValExtExpOD',
    metricCode: 'netSlsMkpFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'netSlsMkpFeeRateExtExpOD',
    metricCode: 'netSlsMkpFeeRate',
    timeRef: 'OD',
  },
  {
    code: 'slsPayFeeValOD',
    metricCode: 'slsPayFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'slsPayFeeRateExtOD',
    metricCode: 'slsPayFeeRate',
    timeRef: 'OD',
  },
  {
    code: 'netSlsLogCostValExtExpOD',
    metricCode: 'netSlsLogCostVal',
    timeRef: 'OD',
  },
  {
    code: 'retShpCstValExtExpOD',
    metricCode: 'retShpCstVal',
    timeRef: 'OD',
  },
  {
    code: 'retShpCstRateExtExpOD',
    metricCode: 'retShpCstRate',
    timeRef: 'OD',
  },
  {
    code: 'retHdlCstValExtExpOD',
    metricCode: 'retHdlCstVal',
    timeRef: 'OD',
  },
  {
    code: 'retHdlCstRateExtExpOD',
    metricCode: 'retHdlCstRate',
    timeRef: 'OD',
  },
  {
    code: 'slsShpCstValOD',
    metricCode: 'slsShpCstVal',
    timeRef: 'OD',
  },
  {
    code: 'slsShpCstRateExtOD',
    metricCode: 'slsShpCstRate',
    timeRef: 'OD',
  },
  {
    code: 'slsHdlCstValOD',
    metricCode: 'slsHdlCstVal',
    timeRef: 'OD',
  },
  {
    code: 'slsHdlCstRateExtOD',
    metricCode: 'slsHdlCstRate',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsOmvValExtOD',
    metricCode: 'itemSlsOmvVal',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsGmvValExtOD',
    metricCode: 'itemSlsGmvVal',
    timeRef: 'OD',
  },
  {
    code: 'slsMkdRateOD',
    metricCode: 'slsMkdRate',
    timeRef: 'OD',
  },
  {
    code: 'slsAtOmvQtyRateOD',
    metricCode: 'slsAtOmvQtyRate',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsMkdRateExtOD',
    metricCode: 'itemSlsMkdRate',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsMkdValExtOD',
    metricCode: 'itemSlsMkdVal',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsShpCostValOD',
    metricCode: 'slsShpCstVal',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsHdlCostValOD',
    metricCode: 'slsHdlCstVal',
    timeRef: 'OD',
  },
  {
    code: 'itemSlsPayFeeValOD',
    metricCode: 'slsPayFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'itemNetSlsMkpFeeValExtExpOD',
    metricCode: 'netSlsMkpFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'itemNetSlsFeeValExtExpOD',
    metricCode: 'netSlsFeeVal',
    timeRef: 'OD',
  },
  {
    code: 'itemRetShpCostValExtExpOD',
    metricCode: 'retShpCstVal',
    timeRef: 'OD',
  },
  {
    code: 'itemRetHdlCostValExtExpOD',
    metricCode: 'retHdlCstVal',
    timeRef: 'OD',
  },
  {
    code: 'itemNetSlsLogCostValExtExpOD',
    metricCode: 'netSlsLogCostVal',
    timeRef: 'OD',
  },
  {
    code: 'itemNetSlsCogValExtExpOD',
    metricCode: 'netSlsCogVal',
    timeRef: 'OD',
  },
  // Still to be replaced
  {
    code: 'itemNetSlsGmvValExpOD',
    metricCode: 'itemNetSlsGmvVal',
    timeRef: 'OD',
  },
] as const;

export interface Metric {
  code: string;
  name: string;
  translationId: string;
  unit: MeasureUnitCode;
  interpretation: InterpretationValue;
}

export type MeasureValues = Record<string, number>;

export interface Measure {
  code: string;
  metric: Metric;
  labelId?: string;
  getValue: (values?: MeasureValues) => number;
  longName: string;
}

// Append (Expected) label to those Measures in the info drawer
export const EXPECTED_LABEL_MEASURE_CODES = [
  'netSlsGmvValExpOD',
  'netSlsGmvValExtExpOD',
  'retShpCstValExtExpOD',
  'retHdlCstValExtExpOD',
  'retQtyRateExpOD',
  'retGmvValExpOD',
] as const satisfies readonly MeasureCode[];

export function isExpectedLabelMeasureCode(code: string): boolean {
  return (
    code.replace(/(?:OD|ED)$/, '').endsWith('Exp') && (EXPECTED_LABEL_MEASURE_CODES as readonly string[]).includes(code)
  );
}

// Measures that should be moved into the info drawer's highlighted "Recent Changes" section
export const RECENTLY_CHANGED_MEASURE_CODES = [
  'netSlsGmvValExpOD',
  'netSlsGmvValExtExpOD',
  'retShpCstValExtExpOD',
  'retHdlCstValExtExpOD',
  'retQtyRateExpOD',
  'retGmvValExpOD',
] as const satisfies readonly MeasureCode[];

export function isRecentlyChangedMeasureCode(code: string): boolean {
  return (
    code.replace(/(?:OD|ED)$/, '').endsWith('Exp') &&
    (RECENTLY_CHANGED_MEASURE_CODES as readonly string[]).includes(code)
  );
}

export class MeasureUtil {
  static divideOrZero(measure: Measure, byMeasure: Measure, values: MeasureValues) {
    const divider = byMeasure.getValue(values);
    if (!divider) {
      return 0;
    }
    return measure.getValue(values) / divider;
  }
}

export function useModelService<T extends CustomMeasureProvider>(measureProvider: T = {} as T) {
  // hint we should introduce a context to avoid having to translate things over and over again, (or use another state management library if we introduce that)
  const intl = useIntl();

  type MeasureOrCustomMeasureKey = keyof T | MeasureCode;

  const measureMap = useMemo(() => {
    const map = new Map<MeasureOrCustomMeasureKey, Measure>();

    MEASURES.forEach(measureBase => {
      const metric = METRICS.find(m => measureBase.metricCode === m.code);
      const translationId = `model.metric.${metric!.code}${measureBase.explicitTax ? 'Ext' : ''}.name`;
      const name = intl.formatMessage({ id: translationId, defaultMessage: metric!.code });
      const longName = intl.formatMessage({ id: translationId, defaultMessage: measureBase.code });

      map.set(measureBase.code, {
        ...measureBase,
        metric: {
          ...metric,
          translationId: translationId,
          name,
        },
        longName,
        getValue: values => (values ? (values[measureBase.code] ?? 0) : 0),
      } as Measure);
    });
    Object.getOwnPropertyNames(measureProvider).forEach(code => {
      map.set(code, measureProvider[code]);
    });
    return map;
  }, [intl]);

  const attributeMap = useMemo(() => {
    const map = new Map<AttributeCode, Attribute>();
    ATTRIBUTES.forEach(attributeBase => {
      const getRawValue = (values?: AttributeValues) =>
        values ? (values[attributeBase.code] ?? undefined) : undefined;

      const { translate } = attributeBase;
      const getValue = translate
        ? (values?: AttributeValues) => {
            const value = getRawValue(values);
            return value && translate(intl, value);
          }
        : getRawValue;

      map.set(attributeBase.code, {
        ...attributeBase,
        name: intl.formatMessage({
          id: `model.attribute.${attributeBase.code}.name`,
          defaultMessage: attributeBase.code,
        }),
        getRawValue,
        getValue,
        translate: (value: string) => (translate ? translate(intl, value) : value),
      } as Attribute);
    });
    return map;
  }, [intl]);

  const getMeasures = useCallback(
    (codes: MeasureOrCustomMeasureKey[]) =>
      codes.map(code => {
        if (!measureMap.has(code)) {
          throw new Error(`Measure code ${code as string} unknown`);
        }

        return measureMap.get(code)!;
      }),
    [measureMap]
  );

  const getMeasure = useCallback((code: MeasureOrCustomMeasureKey) => getMeasures([code])[0], [getMeasures]);

  const getMeasuresByMetrics = useCallback(
    (codes: MetricCode[], timeRef: TimeRef) =>
      codes
        .map(code => {
          const measure = MEASURES.find(
            m => m.metricCode === code && (m.timeRef === timeRef || (timeRef === 'ED' && m.onlyOD))
          );
          if (!measure || !measureMap.has(measure.code)) {
            throw new Error(`Metric code ${code} for time ref ${timeRef} unknown`);
          }
          return measure;
        })
        .map(measure => measureMap.get(measure.code)!),
    [measureMap]
  );

  const getMeasureByMetric = useCallback(
    (code: MetricCode, timeRef: TimeRef) => getMeasuresByMetrics([code], timeRef)[0],
    [getMeasuresByMetrics]
  );

  const getMeasureDetails = (measure: Measure, isOrderDate = true) => {
    const code = measure.metric.code;
    const explicitTax = MEASURES.find(m => m.code === measure.code)?.explicitTax;
    // fallback chain: Ext-ed -> -ed -> Ext(OD) -> base
    const candidates: string[] = [];
    if (!isOrderDate && explicitTax) {
      candidates.push(`model.metric.${code}Ext-ed.description`);
    }
    if (!isOrderDate) {
      candidates.push(`model.metric.${code}-ed.description`);
    }
    if (explicitTax) {
      candidates.push(`model.metric.${code}Ext.description`);
    }
    candidates.push(`model.metric.${code}.description`);

    const descriptionId = candidates.find(id => intl.messages[id]) ?? candidates[candidates.length - 1];

    return {
      ...measure,
      metric: {
        ...measure.metric,
        description: intl.formatMessage({
          id: descriptionId,
        }),
      },
    };
  };

  const getAttributes = useCallback(
    (codes: AttributeCode[]) =>
      codes.map(code => {
        if (!attributeMap.has(code)) {
          throw new Error(`Attribute code ${code} unknown`);
        }

        return attributeMap.get(code)!;
      }),
    [measureMap]
  );

  const getAttribute = useCallback((code: AttributeCode) => getAttributes([code])[0], [getMeasures]);

  return {
    getMeasures,
    getMeasure,
    getMeasuresByMetrics,
    getMeasureByMetric,
    getMeasureDetails,
    getAttributes,
    getAttribute,
  };
}
